import { useMemo } from 'react';
import { useAccessibilityStore } from '../stores/accessibilityStore';
import { useThemeStore } from '../stores/themeStore';
import { useThemeColors } from '../components/charts/useThemeColors';

/**
 * The default (no colour-vision mode) chart palette, in the project's own
 * palette rather than Tailwind's.
 *
 * Ordered so the first few are the ones a two- or three-series chart will use:
 * ink reads as "the subject", green as "the good outcome", brass as the house
 * accent. Severity tones (terracotta, burgundy) sit later so a neutral chart
 * never accidentally colours a category as a warning.
 *
 * These are TOKEN NAMES — recharts writes colours into SVG presentation
 * attributes, where `var(--co-*)` is not resolved, so `useThemeColors` turns
 * them into concrete values and re-resolves them when the theme flips.
 */
const PALETTE_TOKENS = {
  c0: '--co-ink',
  c1: '--co-green',
  c2: '--co-brass',
  c3: '--co-machine',
  c4: '--co-terracotta',
  c5: '--co-burgundy',
  c6: '--co-ink-3',
  c7: '--co-machine-pale',
} as const;

const CHROME_TOKENS = {
  grid: '--co-line-subtle',
  gridStrong: '--co-line-strong',
  axis: '--co-ink-3',
  axisStrong: '--co-ink',
  surface: '--co-raised',
  line: '--co-line',
  ink: '--co-ink',
  healthy: '--co-green',
  warning: '--co-terracotta',
  critical: '--co-burgundy',
} as const;

/**
 * Provides chart configuration that respects accessibility and theme settings.
 * Use this hook in any chart component to get the right colors/animation settings.
 */
export function useChartAccessibility() {
  const colorBlindMode = useAccessibilityStore((s) => s.colorBlindMode);
  const usePatternFills = useAccessibilityStore((s) => s.usePatternFills);
  const reducedMotion = useAccessibilityStore((s) => s.reducedMotion);
  const highContrast = useAccessibilityStore((s) => s.highContrast);
  const showDataTables = useAccessibilityStore((s) => s.showDataTables);
  const accessibleChartColors = useAccessibilityStore((s) => s.getChartColors());
  const accessibleStatusColors = useAccessibilityStore((s) => s.getStatusColors());
  const heatmapColors = useAccessibilityStore((s) => s.getHeatmapColors());
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const isDark = resolvedTheme === 'dark';

  const palette = useThemeColors(PALETTE_TOKENS);
  const chrome = useThemeColors(CHROME_TOKENS);

  return useMemo(
    () => {
      // When a color-vision mode is active we OVERRIDE explicit (semantic)
      // series colors with the color-blind-safe palette, so the chart visibly
      // adapts. In the default mode we honour whatever color the caller passed.
      //
      // Accessibility outranks theming: the deuteranopia/protanopia/tritanopia
      // palettes are chosen to stay distinguishable, not to match the house
      // colours, so they are used verbatim and only the DEFAULT palette follows
      // the theme.
      const colorBlindActive = colorBlindMode !== 'none';
      const chartColors = colorBlindActive
        ? accessibleChartColors
        : [palette.c0, palette.c1, palette.c2, palette.c3, palette.c4, palette.c5, palette.c6, palette.c7];
      const statusColors = colorBlindActive
        ? accessibleStatusColors
        : { healthy: chrome.healthy, warning: chrome.warning, critical: chrome.critical };

      const paletteAt = (index: number) => chartColors[index % chartColors.length];

      // Resolve a series/segment color: palette in CB mode, else explicit.
      const resolveColor = (index: number, explicit?: string) =>
        colorBlindActive ? paletteAt(index) : explicit || paletteAt(index);

      return {
        colors: chartColors,
        statusColors,
        heatmapColors,
        colorBlindMode,
        colorBlindActive,
        usePatternFills,
        highContrast,
        showDataTables,
        isDark,
        // Recharts animation prop
        isAnimationActive: !reducedMotion,
        animationDuration: reducedMotion ? 0 : 300,
        // Grid/axis colors — high contrast bumps them to stronger tones. Both
        // ends of each pair are theme tokens, so they follow light/dark without
        // a hard-coded gray.
        gridColor: highContrast ? chrome.gridStrong : chrome.grid,
        axisColor: highContrast ? chrome.axisStrong : chrome.axis,
        tooltipBg: chrome.surface,
        tooltipBorder: chrome.line,
        tooltipText: chrome.ink,
        resolveColor,
        // Get color at index (wraps around)
        getColor: (index: number) => paletteAt(index),
        // Resolve a fill: pattern URL when pattern fills are on, else the color.
        // `index` MUST match the index used when rendering <ChartPatternDefs>.
        resolveFill: (index: number, explicit?: string) =>
          usePatternFills ? `url(#pattern-${index})` : resolveColor(index, explicit),
        getFill: (index: number) =>
          usePatternFills ? `url(#pattern-${index})` : paletteAt(index),
      };
    },
    [
      accessibleChartColors,
      accessibleStatusColors,
      heatmapColors,
      colorBlindMode,
      usePatternFills,
      reducedMotion,
      highContrast,
      showDataTables,
      isDark,
      palette,
      chrome,
    ],
  );
}
